// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { generateClient } from "aws-amplify/api";

import { ReactComponent as PlusIcon } from "../../assets/icons/plus.svg";
import { modalViewsConst, setModalState } from "../../store/modalSlice";
import ModalBase from "./ModalBase";
import SettingsRow from "../Settings/SettingsRow";
import ProfilePictureRounded from "../ProfilePictureRounded";
import { Skeleton } from "../Skeleton";
import { listOrganizationsCustom } from "../../graphql/customQueries";

interface ManageOrganizationsModalProps {
  /** Provided by ModalGlobal — closes the modal. */
  onClose: (result?: any) => void;
}

// Admin directory of every organization — logo, member count, click-through to the organization page
const ManageOrganizationsModal: React.FC<ManageOrganizationsModalProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Swap this frame's view to the create form — ModalGlobal reuses one frame across views
  const openCreateOrganization = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.adminCreateOrganization,
        properties: { __modalSize: "md" },
      })
    );
  };
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadOrganizations = async () => {
      try {
        const client = generateClient();
        const { data } = await client.graphql({ query: listOrganizationsCustom });
        if (cancelled) return;
        const items = (data?.listOrganizations?.items || []).slice();
        items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOrganizations(items);
      } catch (err) {
        console.error("Error loading organizations:", err);
        if (!cancelled) setError("Could not load organizations.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadOrganizations();
    return () => {
      cancelled = true;
    };
  }, []);

  const openOrganization = (organizationId) => {
    onClose();
    navigate(`/home/organization/${organizationId}`);
  };

  return (
    <ModalBase title="Manage Organizations" onClose={() => onClose()} size="md">
      <div className="mb-4 flex justify-end">
        <button type="button" onClick={openCreateOrganization} className="eai-btn-cta-toolbar">
          <PlusIcon className="h-3.5 w-3.5 shrink-0" />
          New Organization
        </button>
      </div>
      {loading ? (
        <div className="flex flex-col gap-3" role="status" aria-label="Loading organizations">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 py-2">
              <Skeleton className="h-9 w-9 shrink-0" radius="full" />
              <div className="min-w-0 flex-1">
                <Skeleton className="h-3.5 w-40" />
                <Skeleton className="mt-1.5 h-3 w-56" />
              </div>
              <Skeleton className="h-5 w-20" radius="full" />
            </div>
          ))}
        </div>
      ) : error ? (
        <p className="font-inter text-eai-body-sm text-eai-danger-text">{error}</p>
      ) : organizations.length === 0 ? (
        <p className="font-inter text-eai-body-sm text-eai-ink-muted">No organizations yet.</p>
      ) : (
        <div className="flex flex-col divide-y divide-eai-border">
          {organizations.map((org) => {
            const memberCount = org.members?.items?.length ?? 0;
            return (
              <SettingsRow
                key={org.id}
                title={org.name}
                description={org.description}
                leading={
                  <ProfilePictureRounded
                    className="shrink-0 border border-eai-border"
                    diameter={36}
                    pictureURL={org.profilePictureUrl}
                  />
                }
                trailing={
                  <span className="eai-count-pill shrink-0">
                    {memberCount} {memberCount === 1 ? "member" : "members"}
                  </span>
                }
                onClick={() => openOrganization(org.id)}
              />
            );
          })}
        </div>
      )}
    </ModalBase>
  );
};

export default ManageOrganizationsModal;
